/**
 * Stockage par cookies pour l'authentification inter-sous-domaines.
 *
 * Ce module fournit un stockage par cookies partagé entre sous-domaines, en
 * posant les cookies sur le domaine parent, dérivé du hostname (les deux
 * derniers labels) ou forcé par BUN_PUBLIC_COOKIE_DOMAIN.
 *
 * Les données sont encodées en Base64 — simple obfuscation, PAS un chiffrement.
 * Pour des données sensibles, envisager un chiffrement côté serveur.
 */

/**
 * Encode une chaîne en Base64 (compatible UTF-8).
 */
function encodeBase64(value: string): string {
  try {
    // Prise en charge correcte des caractères UTF-8
    const utf8Value = encodeURIComponent(value).replace(
      /%([0-9A-F]{2})/g,
      (_, p1) => {
        return String.fromCharCode(parseInt(p1, 16));
      },
    );
    return btoa(utf8Value);
  } catch {
    console.error("Failed to encode Base64");
    return "";
  }
}

/**
 * Décode une chaîne Base64 (compatible UTF-8).
 */
function decodeBase64(value: string): string | null {
  try {
    const decoded = atob(value);
    // Restaure les caractères UTF-8
    return decodeURIComponent(
      decoded
        .split("")
        .map((c) => "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2))
        .join(""),
    );
  } catch {
    console.error("Failed to decode Base64");
    return null;
  }
}

export interface CookieOptions {
  days?: number;
  path?: string;
  domain?: string;
  secure?: boolean;
  sameSite?: "Strict" | "Lax" | "None";
}

// Options de cookie par défaut — expiration à 24 heures
const DEFAULT_OPTIONS: CookieOptions = {
  days: 1, // 24 hours
  path: "/",
  secure: true,
  sameSite: "Lax",
};

/**
 * Domaine parent sur lequel partager les cookies entre sous-domaines.
 * En développement (localhost), renvoie undefined pour laisser le comportement
 * par défaut.
 */
function getCookieDomain(): string | undefined {
  // Configuration explicite par variable d'environnement. Le préfixe
  // BUN_PUBLIC_ est ce qui rend la variable lisible côté client.
  const explicitDomain = process.env.BUN_PUBLIC_COOKIE_DOMAIN;
  if (explicitDomain) {
    return explicitDomain;
  }

  // En environnement navigateur
  if (typeof window !== "undefined") {
    const hostname = window.location.hostname;

    // localhost — partage de domaine inutile et impossible
    if (hostname === "localhost" || hostname === "127.0.0.1") {
      return undefined;
    }

    // Extrait le domaine parent du sous-domaine
    // p. ex. ent-struct.exemple.fr -> .exemple.fr
    const parts = hostname.split(".");
    if (parts.length >= 2) {
      // Point initial obligatoire pour que le cookie soit partagé aux sous-domaines
      return `.${parts.slice(-2).join(".")}`;
    }
  }

  return undefined;
}

/**
 * Pose un cookie avec le nom, la valeur et les options donnés.
 */
export function setCookie(
  name: string,
  value: string,
  options: CookieOptions = {},
): void {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const domain = getCookieDomain();

  let cookieString = `${encodeURIComponent(name)}=${encodeURIComponent(value)}`;

  // Expiration
  if (opts.days) {
    const expiryDate = new Date();
    expiryDate.setTime(expiryDate.getTime() + opts.days * 24 * 60 * 60 * 1000);
    cookieString += `; expires=${expiryDate.toUTCString()}`;
  }

  // Chemin
  if (opts.path) {
    cookieString += `; path=${opts.path}`;
  }

  // Domaine, pour le partage entre sous-domaines
  if (domain) {
    cookieString += `; domain=${domain}`;
  }

  // Drapeaux de sécurité
  if (opts.secure) {
    cookieString += "; secure";
  }

  if (opts.sameSite) {
    cookieString += `; samesite=${opts.sameSite}`;
  }

  document.cookie = cookieString;
}

/**
 * Valeur d'un cookie, par son nom.
 */
export function getCookie(name: string): string | null {
  if (typeof document === "undefined") {
    return null;
  }

  const nameEQ = encodeURIComponent(name) + "=";
  const cookies = document.cookie.split(";");

  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i]?.trim();
    if (cookie && cookie.indexOf(nameEQ) === 0) {
      return decodeURIComponent(cookie.substring(nameEQ.length));
    }
  }

  return null;
}

/**
 * Supprime un cookie, par son nom.
 */
export function deleteCookie(name: string, options: CookieOptions = {}): void {
  const opts = { ...options, days: -1 };
  setCookie(name, "", opts);
}

/**
 * Les cookies sont-ils activés dans le navigateur ?
 */
export function areCookiesEnabled(): boolean {
  if (typeof navigator === "undefined") {
    return false;
  }
  return navigator.cookieEnabled;
}

/**
 * Interface de stockage compatible Zustand, adossée aux cookies.
 * Permet au middleware `persist` de Zustand d'utiliser les cookies plutôt que
 * localStorage. Les données sont encodées en Base64 (simple obfuscation).
 */
export function createCookieStorage<T = unknown>(
  storageKey: string,
  options: CookieOptions = {},
) {
  return {
    getItem: (): string | null => {
      const encodedValue = getCookie(storageKey);
      if (!encodedValue) {
        return null;
      }
      // Décodage Base64
      const decoded = decodeBase64(encodedValue);
      return decoded;
    },
    setItem: (_name: string, value: string): void => {
      // Encodage Base64 avant écriture
      const encodedValue = encodeBase64(value);
      setCookie(storageKey, encodedValue, options);
    },
    removeItem: (): void => {
      deleteCookie(storageKey, options);
    },
  };
}

/**
 * Stockage hybride : cookies en production, localStorage en développement.
 * On garde ainsi le partage entre sous-domaines en production sans se
 * compliquer la vie en local sur localhost.
 *
 * En production : cookies encodés en Base64.
 * En développement : localStorage tel quel, aucun encodage nécessaire.
 */
export function createHybridStorage<T = unknown>(
  storageKey: string,
  options: CookieOptions = {},
) {
  const isDevelopment =
    typeof window !== "undefined" &&
    (window.location.hostname === "localhost" ||
      window.location.hostname === "127.0.0.1");

  if (isDevelopment) {
    // localStorage en développement — aucun encodage nécessaire en local
    return {
      getItem: (): string | null => {
        const value = localStorage.getItem(storageKey);
        return value;
      },
      setItem: (_name: string, value: string): void => {
        localStorage.setItem(storageKey, value);
      },
      removeItem: (): void => {
        localStorage.removeItem(storageKey);
      },
    };
  }

  // En production : cookies encodés en Base64, pour le partage inter-sous-domaines
  return createCookieStorage<T>(storageKey, options);
}
